import Client from "..";
import { BreakButton } from "../gui/BreakButton";
import { BrakeUsageBar } from "../gui/BrakeUsageBar";
import { Inventory } from "../gui/Inventory";
import { SpinButton } from "../gui/SpinButton";
import { Items } from "../inventory/Items";
import { Global } from "../utils/Global";
import { GameEvents, Songs } from "../utils/Types";
import { Wheel } from "../wheel/Wheel";
import { DynamicScene } from "./DynamicScene";
import { WheelBrake } from "../wheel/WheelBrake";
import { Charms } from "../inventory/Charms";
import { SpinsLeft } from "../gui/SpinsLeft";
import { Timeline } from "../gui/Timeline";
import { GameState } from "../utils/GameState";
import { Phase, StateMachine } from "../utils/StateMachine";
import { PauseMenu } from "../gui/windows/PauseMenu";
import { ScoreContainer } from "../gui/ScoreContainer";
import { MoneyContainer } from "../gui/MoneyContainer";
import { ItemShop } from "../gui/windows/ItemShop";
import { OpenShopButton } from "../gui/OpenShopButton";
import { ContinueButton } from "../gui/ContinueButton";
import { LevelReport } from "../gui/windows/LevelReport";
import { LevelInfoDisplay } from "../gui/LevelInfoDisplay";
import { Jukebox } from "../utils/Jukebox";

export class GameScene extends DynamicScene {
  itemInventory!: Inventory;
  charmInventory!: Inventory;

  wheel!: Wheel;

  public get shopVisible(): boolean {
    return this.itemShop.visible;
  }

  public updateShopButtonState(isOpen: boolean): void {
    this.openShopButton.updateShopState(isOpen);
  }

  private radius: number = 1;
  private brake?: WheelBrake; // Now optional - can be null when no brake is attached
  private brakeUsageBar!: BrakeUsageBar;

  private backgroundShader?: Phaser.GameObjects.Shader;

  private timeline!: Timeline;
  private levelInfoDisplay!: LevelInfoDisplay;
  private spinButton!: SpinButton;
  private breakButton!: BreakButton;
  private openShopButton!: OpenShopButton;
  private continueButton!: ContinueButton;
  private spinsLeft!: SpinsLeft;
  private scoreContainer!: ScoreContainer;
  private moneyContainer!: MoneyContainer;
  private pauseMenu!: PauseMenu;
  private itemShop!: ItemShop;
  private levelReport!: LevelReport;

  private spacebarReleasedAfterSpin = true;
  private isPaused = false;

  private jukebox!: Jukebox;

  // Camera animation properties
  private cameraAnimationTween?: Phaser.Tweens.Tween;

  constructor() {
    super("GameScene");

    Client.state = new GameState();
    Client.stateMachine = new StateMachine();
  }

  public create(): void {
    const w = this.scale.width;
    const h = this.scale.height;

    // Generate timeline if it doesn't exist -- MOVE THIS
    if (Client.state.timeline.length === 0) {
      Client.state.generateTimeline();
    }

    if (Global.enableShaders) this.setBackgroundShader("shd_background", w, h);

    this.wheel = new Wheel(this);
    this.add.existing(this.wheel);

    // Don't create brake by default - it's now a consumable item
    this.brake = undefined;

    this.brakeUsageBar = new BrakeUsageBar(this).setVisible(false);
    this.add.existing(this.brakeUsageBar);

    this.timeline = new Timeline(this);
    this.add.existing(this.timeline);

    this.levelInfoDisplay = new LevelInfoDisplay(this);
    this.add.existing(this.levelInfoDisplay);

    this.breakButton = new BreakButton(this).setVisible(false);
    this.add.existing(this.breakButton);

    this.spinButton = new SpinButton(this);
    this.add.existing(this.spinButton);

    // Ensure break button renders above spin button
    this.breakButton.setDepth(1000);
    this.spinButton.setDepth(999);

    this.openShopButton = new OpenShopButton(
      this,
      () => {
        this.itemShop.showShop();
        this.openShopButton.updateShopState(true);
      },
      () => {
        this.itemShop.hideShop();
        this.openShopButton.updateShopState(false);
      },
    );
    this.add.existing(this.openShopButton);

    this.continueButton = new ContinueButton(this, () => {
      // Close shop if it's open before continuing
      if (this.itemShop.visible) {
        this.itemShop.hideShop();
      }
      Client.stateMachine.ContinueToNextPhase();
    });
    this.add.existing(this.continueButton);

    this.spinsLeft = new SpinsLeft(this);
    this.add.existing(this.spinsLeft);

    this.scoreContainer = new ScoreContainer(this);
    this.add.existing(this.scoreContainer);

    this.moneyContainer = new MoneyContainer(this);
    this.add.existing(this.moneyContainer);

    // Create item shop
    this.itemShop = new ItemShop(this);
    this.add.existing(this.itemShop);

    // Create pause menu
    this.pauseMenu = new PauseMenu(this);
    this.add.existing(this.pauseMenu);

    // Create level report
    this.levelReport = new LevelReport(this);
    this.add.existing(this.levelReport);

    this.itemInventory = new Inventory(
      this,
      Client.state.itemInventory.maxInventorySize,
      false,
    );
    this.itemInventory.LoadState(Client.state.itemInventory.items);
    this.itemInventory.setDepth(10000); // Above item shop
    this.add.existing(this.itemInventory);

    Client.state.itemInventory.AddItemToInventory(Items.CAR_BRAKE);
    Client.state.itemInventory.AddItemToInventory(Items.SCREW);
    Client.state.itemInventory.AddItemToInventory(Items.SCREW_DRIVER);

    // Manually refresh the GUI to show the new items
    this.itemInventory.LoadState(Client.state.itemInventory.items);

    this.charmInventory = new Inventory(
      this,
      Client.state.charmInventory.maxInventorySize,
      true,
    );
    this.charmInventory.LoadState(Client.state.charmInventory.items);
    this.charmInventory.setDepth(10000); // Above item shop
    this.add.existing(this.charmInventory);

    this.input.keyboard!.on("keydown-SPACE", () => {
      if (this.isPaused) return; // Don't allow game actions when paused

      if (Client.state!.wheelSpinning) {
        // Only allow braking if spacebar was released after the spin started AND brake is attached
        if (
          Client.state.phase === Phase.SPINNING &&
          this.spacebarReleasedAfterSpin &&
          this.brake
        ) {
          this.game.events.emit(GameEvents.BREAK_WHEEL_ON);
        }
      } else if (this.spacebarReleasedAfterSpin) {
        // Spin if wheel is not spinning
        Client.stateMachine.SpinWheel();
      }

      this.spacebarReleasedAfterSpin = false;
    });

    // Stop braking when spacebar is released and reset spin capability
    this.input.keyboard!.on("keyup-SPACE", () => {
      if (this.isPaused) return; // Don't allow game actions when paused

      if (Client.state!.wheelSpinning)
        this.game.events.emit(GameEvents.BREAK_WHEEL_OFF);
      this.spacebarReleasedAfterSpin = true;
    });

    // Escape key to toggle pause menu
    const escapeKey = this.input.keyboard!.addKey(
      Phaser.Input.Keyboard.KeyCodes.ESC,
    );
    escapeKey.on("down", () => {
      this.togglePause();
    });

    // Debug controls for adding/removing brake
    if (Global.debug) {
      this.setupDebugControls();
    }

    this.repositionElements(w, h);

    this.game.events.on(
      GameEvents.WHEEL_SPINNING_STATE_CHANGED,
      this.onWheelSpinningStateChanged,
      this,
    );

    this.game.events.on(GameEvents.PHASE_UPDATED, this.onPhaseUpdated, this);
    this.onPhaseUpdated(Client.state.phase);

    this.game.events.on(
      GameEvents.ITEM_INVENTORY_UPDATED,
      this.onItemInventoryUpdated,
      this,
    );
    this.game.events.on(
      GameEvents.CHARM_INVENTORY_UPDATED,
      this.onCharmInventoryUpdated,
      this,
    );
    this.game.events.on(GameEvents.BREAK_WHEEL_ON, this.onBrakeStart, this);
    this.game.events.on(GameEvents.BREAK_WHEEL_OFF, this.onBrakeStop, this);

    // Initialize camera animation if setting enabled
    if (Global.enableCameraBobbing) {
      this.initializeCameraAnimation();
    }

    this.jukebox = new Jukebox(this);
    this.jukebox.PlaySong(Songs.SONG_1);

    this.isReady = true;
  }

  override update(time: number, delta: number): void {
    super.update(time, delta);

    // Update brake usage bar and brake heat if wheel is spinning and brake exists
    if (Client.state.wheelSpinning && this.brake) {
      const brakeUsage = this.wheel.getBrakeUsage();
      this.brakeUsageBar.updateUsage(brakeUsage);
      this.brake.updateHeat(brakeUsage);

      // Stop braking effects if brake is overheated
      if (brakeUsage >= this.wheel.getMaxBrakeUsage()) {
        this.brake.stopBrakingEffects();
      }

      // Remove brake when it reaches 100% usage
      if (
        brakeUsage >= this.wheel.getMaxBrakeUsage() &&
        !this.brake.getIsAnimating()
      ) {
        this.removeBrake();
      }
    }
  }

  public setBackgroundShader(shaderKey: string, w: number, h: number): void {
    if (!Global.enableShaders) return;

    // If an old one exists, destroy it:
    this.backgroundShader?.destroy();

    // Pull the loaded GLSL source out of the cache:
    const fragSource = this.cache.shader.get(shaderKey).glsl;

    // Create the Shader with your fragmentSource and any uniforms:
    this.backgroundShader = this.add.shader(
      {
        name: shaderKey,
        fragmentSource: fragSource,
        initialUniforms: {
          resolution: { type: "2f", value: [w, h] },
        },
        setupUniforms: (
          setUniform: (name: string, value: unknown) => void,
          _drawingContext: unknown,
        ) => {
          setUniform("time", this.game.loop.getDuration());
        },
      },
      w / 2, // x-pos
      h / 2, // y-pos
      w, // width
      h, // height
      [], // textures
    );

    // Tidy up ordering:
    this.backgroundShader.setDepth(-1000); // Ensure it's behind everything
  }

  override repositionElements(width: number, height: number): void {
    const padding = Math.min(width, height) * 0.01;

    // Update background shader size if it exists
    if (Global.enableShaders && this.backgroundShader) {
      this.setBackgroundShader("shd_background", width, height);
    }

    this.radius = Math.floor(Math.min(width, height) * 0.3);
    this.wheel.Resize(this.radius); // Resize should be called after setPosition, since WheelPointer depends on wheel position
    this.wheel.setPosition(width * 0.5, height * 0.5);
    this.wheel.pointer.draw();
    this.wheel.updateDebugGraphics(); // Update debug graphics after position is set

    // Only position brake if it exists
    this.positionBrake();

    let availableBottomSpace =
      (height - this.wheel.displayHeight) * 0.5 - padding * 2;
    const availableRightSpace =
      (width - this.wheel.displayHeight) * 0.5 - padding * 2;

    const buttonHeight = availableBottomSpace * 0.4;
    const buttonWidth = Math.min(buttonHeight * 4, width * 0.5);
    const buttonSpacing = buttonWidth * 0.1;

    if (
      this.spinButton &&
      this.breakButton &&
      this.openShopButton &&
      this.continueButton
    ) {
      // Resize all buttons
      this.spinButton.Resize(buttonWidth, buttonHeight);
      this.breakButton.Resize(buttonWidth, buttonHeight);
      this.openShopButton.Resize(buttonWidth, buttonHeight);
      this.continueButton.Resize(buttonWidth, buttonHeight);

      // Position spin button in center
      this.spinButton.setPosition(
        width * 0.5,
        height - buttonHeight * 0.5 - padding,
      );

      // Position break button on top of spin button
      this.breakButton.setPosition(this.spinButton.x, this.spinButton.y);

      // Position open shop and continue buttons next to each other, centered
      const buttonPairCenterX = width * 0.5;
      const halfSpacing = buttonSpacing * 0.5;

      this.openShopButton.setPosition(
        buttonPairCenterX - buttonWidth * 0.5 - halfSpacing,
        height - buttonHeight * 0.5 - padding,
      );

      this.continueButton.setPosition(
        buttonPairCenterX + buttonWidth * 0.5 + halfSpacing,
        height - buttonHeight * 0.5 - padding,
      );
    }

    availableBottomSpace -= buttonHeight;

    this.itemInventory.Resize(width * 0.8, availableBottomSpace - padding);
    this.itemInventory.setPosition(
      (width - this.itemInventory.width) * 0.5,
      height - this.itemInventory.height - buttonHeight - padding * 2,
    );

    this.charmInventory.Resize(
      Math.min(
        availableRightSpace * 0.5,
        Math.max(availableRightSpace * 0.2, this.itemInventory.height),
      ),
      height * 0.6,
    );
    this.charmInventory.setPosition(
      width - this.charmInventory.width - padding,
      (height - this.charmInventory.height) * 0.5,
    );

    this.spinsLeft.setPosition(
      this.spinButton.x +
        this.spinButton.displayWidth * 0.5 +
        this.spinButton.displayWidth * 0.1,
      this.spinButton.y - this.spinsLeft.displayHeight * 0.5,
    );

    // Position level info display at the top-left corner
    if (this.levelInfoDisplay) {
      const levelInfoMaxWidth = width * 0.25;
      const levelInfoMaxHeight = height * 0.075; // Same height as main score element (0.125 * 0.6)
      this.levelInfoDisplay.Resize(levelInfoMaxWidth, levelInfoMaxHeight);
      this.levelInfoDisplay.setPosition(padding, padding);
    }

    if (this.timeline) {
      this.timeline.Resize(width * 0.05, height - padding * 2);
      this.timeline.setPosition(padding, padding);
    }

    this.scoreContainer.Resize(width * 0.3, height * 0.125);
    this.scoreContainer.setPosition(
      width * 0.5 - this.scoreContainer.width * 0.5,
      padding,
    );

    // Position money container in top right corner
    this.moneyContainer.Resize(width * 0.15, height * 0.08);
    this.moneyContainer.setPosition(
      width - this.moneyContainer.width - padding,
      padding,
    );

    // Position and size item shop
    if (this.itemShop) {
      this.positionItemShop(width, padding);
    }
  }

  private positionItemShop(width: number, padding: number): void {
    // Calculate shop dimensions based on the specified requirements:
    // Height: from bottom inventory to top credits display
    // Width: from wheel center - radius to charms

    // Calculate wheel position and dimensions
    const wheelCenterX = width * 0.5;
    const wheelRadius = this.radius;

    const shopLeft = wheelCenterX - wheelRadius;
    const shopRight = this.charmInventory.x - padding;
    const shopTop =
      this.moneyContainer.y + this.moneyContainer.height + padding;
    const shopBottom = this.itemInventory.y - padding;

    // Calculate final dimensions
    const shopWidth = shopRight - shopLeft;
    const shopHeight = shopBottom - shopTop;
    const shopX = shopLeft + shopWidth / 2;
    const shopY = shopTop + shopHeight / 2;

    // Resize and position the shop
    this.itemShop.resize(shopWidth, shopHeight);
    this.itemShop.setPosition(shopX, shopY);
    this.itemShop.positionElements();
  }

  private onWheelSpinningStateChanged(spinning: boolean): void {
    console.log("onWheelSpinningStateChanged:", spinning, "brake exists:", this.brake !== undefined);

    // Only update UI visibility if not in SHOP state
    if (Client.state.phase !== Phase.STORE) {
      this.spinButton.setVisible(!spinning);
      // Only show break button if we have a brake attached and wheel is spinning
      this.breakButton.setVisible(spinning && this.brake !== undefined);
      // Only show brake usage bar if we have a brake attached and wheel is spinning
      this.brakeUsageBar.setVisible(spinning && this.brake !== undefined);

      console.log("UI updated - spinButton visible:", this.spinButton.visible, "breakButton visible:", this.breakButton.visible);
      console.log("Button depths - spinButton:", this.spinButton.depth, "breakButton:", this.breakButton.depth);
    }

    if (!spinning) {
      // Only stop brake effects if brake exists
      if (this.brake) {
        this.brake.stopBrakingEffects();
      }
    }
  }

  private onPhaseUpdated(phase: Phase): void {
    const isInShop = phase === Phase.STORE;

    // Show/hide UI elements based on shop state
    if (isInShop) {
      // In shop: show shop buttons, hide game UI
      this.openShopButton.setVisible(true);
      this.continueButton.setVisible(true);

      // Reset shop button state when entering shop phase
      this.openShopButton.updateShopState(false);

      // Reset shop state and generate fresh stock for the new shop level
      this.itemShop.resetShopForNewLevel();

      // Auto-open the item shop when entering store phase
      this.itemShop.showShop();
      this.openShopButton.updateShopState(true);

      // Hide all game UI elements
      this.spinButton.setVisible(false);
      this.spinsLeft.setVisible(false);
      this.scoreContainer.setVisible(false);
      this.breakButton.setVisible(false);
      this.brakeUsageBar.setVisible(false);
    } else {
      // Not in shop: hide shop buttons, show game UI
      this.openShopButton.setVisible(false);
      this.continueButton.setVisible(false);

      // Close item shop when leaving store phase
      if (this.itemShop.visible) {
        this.itemShop.hideShop();
        this.openShopButton.updateShopState(false);
      }

      // Show game UI elements
      this.spinButton.setVisible(true);
      this.spinsLeft.setVisible(true);
      this.scoreContainer.setVisible(true);
      // Note: breakButton and brakeUsageBar visibility is handled by onWheelSpinningStateChanged
      // based on wheel spinning state and brake availability

      // Re-enable wheel interactions when leaving shop phase
      this.wheel?.enableAllInteractions();
    }
  }

  private onItemInventoryUpdated(): void {
    this.itemInventory.LoadState(Client.state.itemInventory.items);
  }

  private onCharmInventoryUpdated(): void {
    this.charmInventory.LoadState(Client.state.charmInventory.items);
  }

  private onBrakeStart(): void {
    if (
      this.brake &&
      Client.state.wheelSpinning &&
      this.wheel.getBrakeUsage() < this.wheel.getMaxBrakeUsage()
    ) {
      this.brake.startBrakingEffects();
    }
  }

  private onBrakeStop(): void {
    if (this.brake) {
      this.brake.stopBrakingEffects();
    }
  }

  /**
   * Add a brake to the wheel (consumable item)
   */
  public addBrake(): boolean {
    // Don't allow adding if a brake already exists or is animating
    if (this.brake) {
      return false;
    }

    this.brake = new WheelBrake(this);
    this.add.existing(this.brake);

    // Reset wheel brake usage when adding a new brake
    this.wheel.resetBrakeUsage();

    // Reposition elements to include the new brake
    this.positionBrake();

    // Play assembly animation
    this.brake.playAssemblyAnimation(() => {
      // Animation complete - brake is now fully assembled
    });

    return true;
  }

  private positionBrake(): void {
    if (this.brake) {
      this.brake.Resize(this.radius / 2, this.radius);
      this.brake.setPosition(this.wheel.x + this.radius, this.wheel.y);

      // Position brake heat  bar to the right of the brake
      this.brakeUsageBar.setSize(20, Math.min(this.radius * 1.5, 200));
      this.brakeUsageBar.setPosition(
        this.brake.x + this.brake.displayWidth * 0.6,
        this.brake.y,
      );
    }
  }

  /**
   * Remove the brake from the wheel
   */
  public removeBrake(): void {
    if (!this.brake) {
      return;
    }

    // Don't allow removal if already animating
    if (this.brake.getIsAnimating()) {
      return;
    }

    // Hide brake-related UI immediately
    this.breakButton.setVisible(false);
    this.brakeUsageBar.setVisible(false);

    // Play disassembly animation
    this.brake.playDisassemblyAnimation(() => {
      // Animation complete - now actually remove the brake
      if (this.brake) {
        this.brake.destroy();
        this.brake = undefined;
      }
    });
  }

  /**
   * Toggle pause state
   */
  private togglePause(): void {
    if (this.isPaused) {
      this.unpause();
    } else {
      this.pause();
    }
  }

  /**
   * Show the level report window for success
   */
  public showLevelReport(levelData: {
    spinsUsed: number;
    baseMoney: number;
    bonusMoney: number;
    requiredScore: number;
    achievedScore: number;
  }): void {
    this.levelReport.showLevelReport(levelData, true, () => {
      // Show the continue button again after level report is closed
      this.continueButton.setVisible(true);

      if (Client.state.level > Client.state.timeline.length) {
        // Game completed - go back to main menu
        this.returnToMainMenu();
      } else {
        // Continue to next level
        Client.stateMachine.ContinueToNextPhase();
      }
    });
  }

  /**
   * Show the level report window for failure
   */
  public showLevelFailure(levelData: {
    spinsUsed: number;
    baseMoney: number;
    bonusMoney: number;
    requiredScore: number;
    achievedScore: number;
  }): void {
    this.levelReport.showLevelReport(levelData, false, () => {
      // Go back to main menu on failure
      this.returnToMainMenu();
    });
  }

  /**
   * Pause the game
   */
  private pause(): void {
    if (this.isPaused) return;

    this.isPaused = true;

    // Disable wheel interactions when paused (same as store mode)
    this.wheel?.disableAllInteractions();

    // Disable inventory interactions when paused
    this.itemInventory?.disableAllInteractions();
    this.charmInventory?.disableAllInteractions();

    // Show pause menu first
    this.pauseMenu.showPauseMenu(
      () => this.unpause(), // Continue callback
      () => this.returnToMainMenu(), // Main menu callback
    );

    // Pause game systems but NOT the scene itself (so UI remains interactive)
    this.physics.pause();

    // Pause specific tweens but exclude the pause menu
    this.tweens.getTweens().forEach((tween: Phaser.Tweens.Tween) => {
      // Don't pause tweens that belong to the pause menu
      if (tween.targets && !tween.targets.includes(this.pauseMenu)) {
        tween.pause();
      }
    });

    // Pause time for shaders and other time-based effects
    this.time.paused = true;
  }

  /**
   * Unpause the game
   */
  private unpause(): void {
    if (!this.isPaused) return;

    this.isPaused = false;

    // Re-enable wheel interactions when unpaused, but only if shop window is not visible
    if (!this.itemShop.visible) {
      this.wheel?.enableAllInteractions();
    }

    // Re-enable inventory interactions when unpaused
    this.itemInventory?.enableAllInteractions();
    this.charmInventory?.enableAllInteractions();

    // Resume all game systems
    this.physics.resume();

    // Resume all paused tweens
    this.tweens.getTweens().forEach((tween: Phaser.Tweens.Tween) => {
      if (tween.isPaused()) {
        tween.resume();
      }
    });

    // Resume time for shaders and other time-based effects
    this.time.paused = false;

    // Hide pause menu
    this.pauseMenu.hidePauseMenu();
  }

  /**
   * Return to main menu with hard reset
   */
  private returnToMainMenu(): void {
    // Perform hard reset of the Phaser game
    Client.game!.destroy(true, false);
    Client.game = new Phaser.Game(Global.gameConfig);
  }

  /**
   * Setup debug controls
   */
  private setupDebugControls(): void {
    if (!this.input.keyboard) return;

    // Add brake with C key
    let selectedIndex = 0;
    this.input.keyboard.on("keydown", (event: KeyboardEvent) => {
      switch (event.key) {
        case "1":
          Client.state.itemInventory.AddItemToInventory(Items.HOLEPUNCHER);
          break;
        case "2":
          Client.state.itemInventory.AddItemToInventory(Items.KNIFE);
          break;
        case "3":
          Client.state.itemInventory.AddItemToInventory(Items.BOXCUTTER);
          break;
        case "4":
          Client.state.charmInventory.AddItemToInventory(Charms.PILL);
          break;
        case "t":
          Client.game.events.emit(GameEvents.LEVEL_ACTION_TRIGGERED);
          break;
        case "0":
          if (this.wheel.sections[selectedIndex])
            Client.stateMachine.OnSpinCompleted(
              this.wheel.sections[selectedIndex],
            );
          break;
        case "+":
          selectedIndex++;
          console.log("Selected section index:", selectedIndex);
          break;
        case "-":
          selectedIndex--;
          console.log("Selected section index:", selectedIndex);
          break;
        case "B":
        case "b":
          this.debugMoveLevel(-1); // Move backwards
          break;
        case "N":
        case "n":
          this.debugMoveLevel(1); // Move forwards
          break;
        case "S":
        case "s":
          this.debugIncreaseTimelineVisibility();
          break;
        case "A":
        case "a":
          this.debugDecreaseTimelineVisibility();
          break;
        case "D":
        case "d":
          Client.state.phase = Phase.STORE;
          break;
        case "M":
        case "m":
          Client.state.money += 100; // Add $100 for testing
          break;
        case "L":
        case "l":
          Client.state.money -= 50; // Remove $50 for testing
          break;
        case "P":
        case "p":
          Client.state.score += 500; // Add score for testing bonus money
          break;
      }
    });

    // Add brake with C key
    this.input.keyboard.on("keydown-C", () => {
      this.addBrake();
    });

    // Remove brake with V key
    this.input.keyboard.on("keydown-V", () => {
      this.removeBrake();
    });

    console.warn("* C: Add brake to wheel");
    console.warn("* V: Remove brake from wheel");
    console.warn("* 1: Add BRAKE item to inventory");
    console.warn("* 2: Add EXTRA SPIN item to inventory");
    console.warn("* 3: Add MULTIPLY SECTION SCORE item to inventory");
    console.warn("* 4: Add FORTUNE COOKIE item to inventory");
    console.warn("* 0: Trigger OnSpinCompleted for selected section");
    console.warn("* +/-: Change the index of selected section");
    console.warn("* T: Emit LEVEL_ACTION_TRIGGERED");
    console.warn("* B: Move to previous level");
    console.warn("* N: Move to next level");
    console.warn("* A: Increase timeline visibility");
    console.warn("* S: Decrease timeline visibility");
  }

  /**
   * Debug method to move player one level forwards or backwards
   * @param direction 1 for forward, -1 for backward
   */
  private debugMoveLevel(direction: number): void {
    if (!Client.state) return;

    const currentLevel = Client.state.level;
    const timeline = Client.state.timeline;

    // Calculate new level with overflow protection
    const newLevel = currentLevel + direction;

    // Check bounds - levels are 1-indexed and should not exceed timeline length
    if (newLevel < 1) {
      console.warn(
        `Debug: Cannot move to level ${newLevel}. Minimum level is 1.`,
      );
      return;
    }

    if (newLevel > timeline.length) {
      console.warn(
        `Debug: Cannot move to level ${newLevel}. Maximum level is ${timeline.length}.`,
      );
      return;
    }

    // Update the level
    Client.state.level = newLevel;

    console.log(`Debug: Moved to level ${newLevel} of ${timeline.length}`);
    console.log(`Current level info:`, timeline[newLevel - 1]); // timeline is 0-indexed, level is 1-indexed
  }

  /**
   * Debug method to increase timeline visibility
   */
  private debugIncreaseTimelineVisibility(): void {
    if (!Client.state) return;

    const currentVisibility = Client.state.timelineVisibility;
    const newVisibility = currentVisibility + 1;

    Client.state.timelineVisibility = newVisibility;

    // Update the timeline display by forcing a resize (which redraws it)
    if (this.timeline) {
      const padding = Math.min(this.scale.width, this.scale.height) * 0.01;
      this.timeline.Resize(
        this.scale.width * 0.05,
        this.scale.height - 2 * padding,
      );
    }

    console.log(
      `Debug: Timeline visibility increased from ${currentVisibility} to ${newVisibility}`,
    );
  }

  /**
   * Debug method to decrease timeline visibility
   */
  private debugDecreaseTimelineVisibility(): void {
    if (!Client.state) return;

    const currentVisibility = Client.state.timelineVisibility;
    const newVisibility = Math.max(0, currentVisibility - 1);

    Client.state.timelineVisibility = newVisibility;

    // Update the timeline display by forcing a resize (which redraws it)
    if (this.timeline) {
      const padding = Math.min(this.scale.width, this.scale.height) * 0.01;
      this.timeline.Resize(
        this.scale.width * 0.05,
        this.scale.height - 2 * padding,
      );
    }

    console.log(
      `Debug: Timeline visibility decreased from ${currentVisibility} to ${newVisibility}`,
    );
  }

  private initializeCameraAnimation(): void {
    // Stop any existing animation
    if (this.cameraAnimationTween) {
      this.cameraAnimationTween.destroy();
    }

    // Define the subtle movement parameters
    const maxOffset = 3; // Maximum pixels to move from center
    const duration = 10000; // 8 seconds for a full cycle
    const easing = "Sine.easeInOut"; // Smooth easing

    // Create a looping tween that moves the camera in a subtle figure-8 pattern
    this.cameraAnimationTween = this.tweens.add({
      targets: this.cameras.main,
      scrollX: {
        from: -maxOffset,
        to: maxOffset,
      },
      scrollY: {
        from: -maxOffset * 0.5,
        to: maxOffset * 0.5,
      },
      duration: duration,
      ease: easing,
      yoyo: true,
      repeat: -1, // Infinite loop
      onUpdate: () => {
        // Add a slight rotation to the movement for more organic feel
        const time = this.time.now * 0.0005; // Very slow rotation
        const offsetX = Math.sin(time) * maxOffset * 0.3;
        const offsetY = Math.cos(time * 0.7) * maxOffset * 0.2;

        this.cameras.main.scrollX += offsetX * 0.1;
        this.cameras.main.scrollY += offsetY * 0.1;

        // Move the background shader along with the camera to prevent artifacts
        if (this.backgroundShader) {
          this.backgroundShader.x =
            this.scale.width / 2 + this.cameras.main.scrollX;
          this.backgroundShader.y =
            this.scale.height / 2 + this.cameras.main.scrollY;
        }
      },
    });
  }
}
